import { Action, Location, History } from 'history';
import { ActionMap, createMsg } from 'src/core/engine/actionCreators';
import { Middleware, UnknownAction } from 'redux';

import { createDebug } from '../utils/runtimeDebug';
import { ResolvedUrl } from '../utils/resolve';

import { HistoryEvent } from './AsyncRouter';

const debug = createDebug('AsyncRouter:register');

export interface RouterState {
    location: Location;
    action: Action | null;
    resolving: boolean;
    current: ResolvedUrl | null;
}

export const initialState: RouterState = {
    resolving: false,
    action: null,
    current: null,
    location: {
        pathname: '',
        search: '',
        state: undefined,
        hash: '',
        key: 'default',
    },
};

type Messages = {
    ['AsyncRouter.CHANGE']: HistoryEvent;
    ['AsyncRouter.@@INTERNAL']: { method: string; args: any[] };
    ['AsyncRouter.Resolved']: undefined;
    ['AsyncRouter.Complete']: ResolvedUrl;
};

export const LOCATION_CHANGE = 'AsyncRouter.CHANGE';
export const LOCATION_CHANGE_INTERNAL = 'AsyncRouter.@@INTERNAL';

export function asyncRouter(state = initialState, action: Actions): RouterState {
    switch (action.type) {
        case 'AsyncRouter.CHANGE': {
            return {
                ...state,
                ...action.payload,
                resolving: true,
            };
        }
        case 'AsyncRouter.Resolved': {
            return {
                ...state,
                resolving: false,
            };
        }
        case 'AsyncRouter.Complete': {
            return {
                ...state,
                current: { ...action.payload },
            };
        }
        default:
            return state;
    }
}

export function push(path: string, state?: any) {
    return RouterMsg('AsyncRouter.@@INTERNAL', {
        method: 'push',
        args: [path, state],
    });
}

export function replace(path: string, state?: any) {
    return RouterMsg('AsyncRouter.@@INTERNAL', {
        method: 'replace',
        args: [path, state],
    });
}

export const Msg = createMsg<Messages>();
export const RouterMsg = Msg;
export type TypeMap = ActionMap<Messages>;
export type Actions = TypeMap[keyof TypeMap];
export type RouterActions = Actions;
export type AppendState<T = {}> = T & { router: RouterState };
export type AppendRouterState<T = {}> = T & { router: RouterState };

export function routerRegister() {
    return {
        epics: [],
        name: 'router',
        reducers: {
            router: asyncRouter,
        },
    };
}

type MiddlewareAction = UnknownAction & {
    _subscribe?: unknown;
    payload?: { method: string; args: any[] };
};

export function routerMiddleware(history: History): Middleware {
    return function (_store) {
        return function (next) {
            return function (action: unknown) {
                const act = action as MiddlewareAction;
                if (act._subscribe) {
                    console.error('Saw _subscribe on an action - did you send an observable by mistake?');
                    return;
                }
                if (act.type !== LOCATION_CHANGE_INTERNAL) {
                    return next(action);
                }

                const _action$payload = act.payload;
                if (!_action$payload) {
                    return next(action);
                }
                const method = _action$payload.method;
                const args = _action$payload.args;
                debug(act.type, method, args);
                history[method](...args);
            };
        };
    };
}
